"use client";

// ── Единый модуль авторизованных медиа ──────────────────────────────────────
// Приватные файлы портала (/xxx-files/*, /kb/files/*, /api/*) отдаются только
// с Bearer-токеном — прямой <img src="/api/..."> не работает. Здесь живёт
// ЕДИНСТВЕННАЯ реализация «fetch + Bearer → blob → objectURL»:
//   • useAuthBlobUrl — хук с revoke по жизненному циклу компонента;
//   • cache:true — общий ограниченный кэш (виртуализированные таблицы, где
//     ячейки постоянно перемонтируются); URL из кэша НЕ revoke'ать у себя;
//   • downloadAuthFile / authStreamUrl — скачивание и range-стриминг видео.
// Новые компоненты используют этот модуль, а не локальные копии fetch→blob.

import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import { TOKEN_STORAGE_KEY } from "@/lib/theme";

export type AuthBlobMode = "any" | "files";
export type AuthBlobOptions = { mode?: AuthBlobMode; cache?: boolean };

// Пути приватных файлов бэка. Всё остальное относительное в режиме "files" —
// публичные ассеты Next (например /help-media/*), их отдаём в src напрямую.
const FILES_RE = /^\/(api\/|kb\/files\/|[a-z0-9-]+-files\/)/;

function baseRoot(): string {
  return API_BASE.replace(/\/api$/, "");
}

// null — рисовать нечего; auth:false — URL можно отдать в src напрямую.
function resolve(src: string | null | undefined, mode: AuthBlobMode): { url: string; auth: boolean } | null {
  if (!src) return null;
  if (/^(data:|blob:)/.test(src)) return { url: src, auth: false };
  if (/^https?:\/\//.test(src)) {
    // Абсолютные URL нашего бэка (api.teamFileUrl и т.п.) — с авторизацией,
    // внешние (WB CDN и др.) — напрямую.
    return { url: src, auth: src.startsWith(`${baseRoot()}/`) };
  }
  if (src.startsWith("/")) {
    if (mode === "any" || FILES_RE.test(src)) return { url: `${baseRoot()}${src}`, auth: true };
    return { url: src, auth: false };
  }
  return { url: src, auth: false };
}

function authHeaders(): HeadersInit {
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Общий кэш objectURL'ов ──────────────────────────────────────────────────
// Map хранит порядок вставки → простое LRU: при переполнении удаляем и
// РЕВОКАЕМ самый старый. "miss" тоже кэшируется, чтобы при скролле таблицы
// не долбить бэк повторными 404.
const CACHE_MAX = 500;
const blobCache = new Map<string, string | "miss">();
const inflight = new Map<string, Promise<string | null>>();

function cachePut(key: string, value: string | "miss") {
  if (blobCache.has(key)) blobCache.delete(key);
  blobCache.set(key, value);
  if (blobCache.size > CACHE_MAX) {
    const oldest = blobCache.entries().next().value as [string, string | "miss"];
    blobCache.delete(oldest[0]);
    if (oldest[1] !== "miss") URL.revokeObjectURL(oldest[1]);
  }
}

/** Синхронный доступ к кэшу — для useState-инициализации (без мигания при перемонтировании ячеек). */
export function peekAuthBlobUrl(src: string | null | undefined, opts: AuthBlobOptions = {}): string | null {
  const r = resolve(src, opts.mode ?? "files");
  if (!r) return null;
  if (!r.auth) return r.url;
  const hit = blobCache.get(r.url);
  return hit && hit !== "miss" ? hit : null;
}

/** Сбросить кэш для src (после перезаливки файла). */
export function invalidateAuthBlobUrl(src: string | null | undefined, opts: AuthBlobOptions = {}): void {
  const r = resolve(src, opts.mode ?? "files");
  if (!r) return;
  const hit = blobCache.get(r.url);
  if (hit) {
    blobCache.delete(r.url);
    if (hit !== "miss") URL.revokeObjectURL(hit);
  }
}

/**
 * Промис blob-URL (null — нет файла/ошибка).
 * cache:true — URL принадлежит общему кэшу, revoke'ать его НЕЛЬЗЯ.
 * cache:false — владение у вызывающего (revoke — его забота).
 */
export async function fetchAuthBlobUrl(src: string | null | undefined, opts: AuthBlobOptions = {}): Promise<string | null> {
  const { mode = "files", cache = false } = opts;
  const r = resolve(src, mode);
  if (!r) return null;
  if (!r.auth) return r.url;

  if (cache) {
    const hit = blobCache.get(r.url);
    if (hit === "miss") return null;
    if (hit) return hit;
    const pending = inflight.get(r.url);
    if (pending) return pending;
    const p = (async () => {
      try {
        const resp = await fetch(r.url, { headers: authHeaders() });
        if (!resp.ok) { cachePut(r.url, "miss"); return null; }
        const url = URL.createObjectURL(await resp.blob());
        cachePut(r.url, url);
        return url;
      } catch {
        cachePut(r.url, "miss");
        return null;
      } finally {
        inflight.delete(r.url);
      }
    })();
    inflight.set(r.url, p);
    return p;
  }

  try {
    const resp = await fetch(r.url, { headers: authHeaders() });
    if (!resp.ok) return null;
    return URL.createObjectURL(await resp.blob());
  } catch {
    return null;
  }
}

/**
 * Хук: blob-URL для приватного файла (+флаг failed для плейсхолдеров).
 * Без cache старый objectURL ревокается при смене src (после готовности
 * нового — без мигания) и на unmount; с cache — живёт в общем кэше.
 */
export function useAuthBlobUrl(
  src: string | null | undefined,
  opts: AuthBlobOptions = {},
): { url: string | null; failed: boolean } {
  const { mode = "files", cache = false } = opts;
  const [url, setUrl] = useState<string | null>(() => peekAuthBlobUrl(src, opts));
  const [failed, setFailed] = useState(false);
  const ownRef = useRef<string | null>(null); // objectURL в нашем владении (cache:false)

  useEffect(() => {
    setFailed(false);
    const r = resolve(src, mode);
    if (!r) { setUrl(null); return; }
    if (!r.auth) { setUrl(r.url); return; }

    let cancelled = false;
    fetchAuthBlobUrl(src, { mode, cache }).then((made) => {
      if (cancelled) {
        if (made && !cache) URL.revokeObjectURL(made);
        return;
      }
      if (!cache) {
        if (ownRef.current) URL.revokeObjectURL(ownRef.current);
        ownRef.current = made;
      }
      setUrl(made);
      setFailed(!made);
    });
    return () => { cancelled = true; };
  }, [src, mode, cache]);

  useEffect(() => () => { if (ownRef.current) URL.revokeObjectURL(ownRef.current); }, []);

  return { url, failed };
}

/** Скачивание приватного файла кликом по временной ссылке. */
export async function downloadAuthFile(url: string, filename: string, opts: AuthBlobOptions = {}): Promise<void> {
  const blobUrl = await fetchAuthBlobUrl(url, { mode: opts.mode ?? "files", cache: false });
  if (!blobUrl) return;
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Ревокать сразу нельзя — скачивание может не успеть начаться.
  if (blobUrl.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
}

/**
 * Прямой URL с токеном в query — для нативного range-стриминга видео
 * (<video src>), без загрузки файла целиком в blob.
 */
export function authStreamUrl(src: string, opts: AuthBlobOptions = {}): string {
  const r = resolve(src, opts.mode ?? "files");
  if (!r || !r.auth) return src;
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  return `${r.url}${r.url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token || "")}`;
}
